import { Theme } from '@material-ui/core/styles';

/**
 * MergeOps design tokens.
 *
 * A single, layered surface/border/state system shared by every MergeOps
 * component. Previously these rgba() literals were copy-pasted across
 * EntityGroupMergeOpsContent, ComponentMergeOpsSection, PrRow and
 * PipelineStages, which made the dark theme drift and read like a flat
 * stack of mid-gray blocks. Centralising them gives us a small, intentional
 * palette:
 *
 *   page        — the page canvas (Backstage owns this)
 *   surface     — panels that sit on the page (flow panel, toolbar)
 *   elevated    — cards that sit on a surface (flow nodes, component cards)
 *   border      — default 1px hairline between layers
 *   borderSoft  — quieter divider inside a card
 *   hover       — teal-tinted hover wash
 *   selected    — teal-tinted selected wash (paired with a left accent bar)
 *   accent      — the Backstage teal brand, used sparingly for "current"
 *
 * State tokens (ready / blocked / caution) carry their own border + fill so
 * badges stay legible in both themes without per-call-site conditionals.
 */
export interface MergeOpsTokens {
  isDark: boolean;
  surface: string;
  surfaceGlass: string;
  elevated: string;
  border: string;
  borderSoft: string;
  hover: string;
  selected: string;
  accent: string;
  badgeBg: string;
  badgeBorder: string;
  state: {
    ready: { fg: string; bg: string; border: string };
    blocked: { fg: string; bg: string; border: string };
    caution: {
      fg: string;
      bg: string;
      border: string;
      solid: string;
      solidHover: string;
    };
  };
  shadow: string;
  // radii / rhythm so spacing & corner rounding stay consistent
  radius: number;
  radiusSm: number;
}

export function mergeOpsTokens(theme: Theme): MergeOpsTokens {
  const isDark = theme.palette.type === 'dark';
  return {
    isDark,
    surface: isDark ? 'rgba(41, 42, 46, 0.78)' : theme.palette.background.paper,
    surfaceGlass: isDark
      ? 'rgba(38, 39, 43, 0.94)'
      : 'rgba(255, 255, 255, 0.94)',
    elevated: isDark ? 'rgba(52, 53, 58, 0.82)' : 'rgba(255, 255, 255, 0.94)',
    border: isDark ? 'rgba(196, 196, 204, 0.2)' : 'rgba(15, 23, 42, 0.12)',
    borderSoft: isDark ? 'rgba(196, 196, 204, 0.16)' : 'rgba(15, 23, 42, 0.1)',
    hover: isDark ? 'rgba(20, 184, 166, 0.1)' : 'rgba(13, 148, 136, 0.06)',
    selected: isDark ? 'rgba(20, 184, 166, 0.16)' : 'rgba(13, 148, 136, 0.08)',
    accent: theme.palette.primary.main,
    badgeBg: isDark ? 'rgba(35, 36, 40, 0.52)' : 'rgba(248, 250, 252, 0.9)',
    badgeBorder: isDark
      ? 'rgba(196, 196, 204, 0.22)'
      : 'rgba(15, 23, 42, 0.12)',
    state: {
      ready: {
        fg: theme.palette.success.main,
        bg: isDark ? 'rgba(34, 197, 94, 0.12)' : 'rgba(22, 163, 74, 0.08)',
        border: isDark ? 'rgba(74, 222, 128, 0.28)' : 'rgba(22, 163, 74, 0.22)',
      },
      blocked: {
        fg: theme.palette.error.main,
        bg: isDark ? 'rgba(239, 68, 68, 0.13)' : 'rgba(220, 38, 38, 0.08)',
        border: isDark
          ? 'rgba(248, 113, 113, 0.34)'
          : 'rgba(220, 38, 38, 0.22)',
      },
      caution: {
        fg: isDark ? 'rgba(253, 230, 138, 0.92)' : 'rgba(120, 53, 15, 0.92)',
        bg: isDark ? 'rgba(217, 119, 6, 0.12)' : 'rgba(180, 83, 9, 0.08)',
        border: isDark ? 'rgba(251, 191, 36, 0.28)' : 'rgba(180, 83, 9, 0.24)',
        solid: isDark ? '#d97706' : '#b45309',
        solidHover: isDark ? '#f59e0b' : '#92400e',
      },
    },
    shadow: isDark
      ? '0 6px 18px rgba(0, 0, 0, 0.24)'
      : '0 6px 18px rgba(15, 23, 42, 0.06)',
    radius: 8,
    radiusSm: 6,
  };
}
